import React, { useEffect, useState, useCallback } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import CrawlFailureBadge from '../../../components/CrawlFailureBadge';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import ListingFieldChanges from '../../../components/listingDetail/Overview/ListingFieldChanges';
import client from '../../../lib/feathers';

/**
 * Run detail — click a row in Listing Data Health to see the per-stage steps,
 * progress, and proposals of a single enrichment run, and (when awaiting_review)
 * approve/reject it.
 *
 * Note: full_chain writes the `stages` blob when the run finishes, so per-stage
 * detail appears on completion. While a run is still active this page shows
 * status + elapsed time and auto-refreshes until it lands.
 */

const OUTCOME_STYLE = {
  ok: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-gray-100 text-gray-800',
  running: 'bg-blue-100 text-blue-800',
  claimed: 'bg-blue-100 text-blue-800',
  pending: 'bg-purple-100 text-purple-800',
  awaiting_review: 'bg-amber-100 text-amber-900',
};

const ACTIVE = new Set(['running', 'claimed', 'pending']);

// Display order of stages in the chain (A-before-B). Unknown stages sort after.
const STAGE_ORDER = [
  '01_crawl', 'verify_identity', '05_listing_fields', 'phase_b_gate',
  '02_extract', '02b_commerce7', '02c_normalize', '03_import', '04_upload_assets',
];
const STAGE_LABEL = {
  '01_crawl': 'Crawl site',
  verify_identity: 'Verify identity',
  '05_listing_fields': 'Listing fields (Phase A)',
  phase_b_gate: 'Phase B gate',
  '02_extract': 'Extract wines',
  '02b_commerce7': 'Commerce7 catalog',
  '02c_normalize': 'Normalize wines',
  '03_import': 'Import wines',
  '04_upload_assets': 'Upload assets',
};

function OutcomePill({ outcome }) {
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${OUTCOME_STYLE[outcome] || 'bg-gray-100 text-gray-800'}`}>
      {outcome}
    </span>
  );
}

function stageStatusIcon(stage) {
  if (!stage || typeof stage !== 'object') return <span className="text-gray-400">•</span>;
  if (stage.ok === true) return <span className="text-green-600">✓</span>;
  if (stage.ok === false) return <span className="text-red-600">✗</span>;
  return <span className="text-gray-400">•</span>;
}

function StageStep({ stageKey, stage }) {
  const label = STAGE_LABEL[stageKey] || stageKey;
  const isGate = stageKey === 'phase_b_gate';
  const isVerify = stageKey === 'verify_identity';
  const proposalData = stage?.proposalData;        // 05 structured listing fields
  const proposalText = stage?.proposal;            // 03/04 text proposals
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="px-3 py-2 flex items-center gap-2 bg-gray-50 rounded-t-lg">
        <span className="text-sm">{stageStatusIcon(stage)}</span>
        <span className="text-sm font-medium text-gray-800">{label}</span>
        <span className="text-xs text-gray-400 font-mono">{stageKey}</span>
        {stage?.durationSec != null && (
          <span className="ml-auto text-xs text-gray-500">{stage.durationSec}s</span>
        )}
      </div>
      <div className="px-3 py-2 space-y-2">
        {stage?.error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-xs font-mono text-red-800 whitespace-pre-wrap break-all">
            {stage.error}
          </div>
        )}
        {isGate && stage && (
          <div className="text-xs text-gray-700">
            {stage.allowed ? 'Phase B allowed' : 'Phase B skipped'}
            {stage.reason ? ` — ${stage.reason}` : ''}
          </div>
        )}
        {isVerify && stage && (
          <div className="text-xs text-gray-700">
            status <span className="font-mono">{stage.status || '—'}</span>
            {stage.confidence ? ` · confidence ${stage.confidence}` : ''}
            {stage.distanceMiles != null ? ` · ${stage.distanceMiles} mi` : ''}
          </div>
        )}
        {proposalData && <ListingFieldChanges proposalData={proposalData} />}
        {proposalText && (
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">
            {proposalText}
          </pre>
        )}
        {!stage?.error && !isGate && !isVerify && !proposalData && !proposalText && (
          <div className="text-xs text-gray-400 italic">no detail captured</div>
        )}
      </div>
    </div>
  );
}

export default function ListingDataHealthRun() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.service('admin/listing-enrichment-run').get(runId);
      setRun(r);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load run');
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while the run is still active so progress updates land.
  useEffect(() => {
    if (!run || !ACTIVE.has(run.outcome)) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [run, load]);

  const approve = async () => {
    if (!window.confirm('Approve this run? The supervisor will write the proposed changes to prod on its next tick.')) return;
    setBusy(true);
    try {
      const patched = await client.service('admin/listing-enrichment-run').patch(runId, {
        approvedAt: new Date().toISOString(),
      });
      if (!patched || !patched.approvedAt) throw new Error('approvedAt did not persist — patch was a no-op');
      await load();
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    const reason = window.prompt('Reject this proposal? Add a short reason:', 'admin rejected');
    if (reason === null) return;
    setBusy(true);
    try {
      await client.service('admin/listing-enrichment-run').patch(runId, {
        outcome: 'skipped',
        error: `admin rejected: ${reason || 'no reason given'}`,
        finishedAt: new Date().toISOString(),
      });
      await load();
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const stages = (run?.stages && typeof run.stages === 'object') ? run.stages : {};
  const orderedKeys = [
    ...STAGE_ORDER.filter((k) => k in stages),
    ...Object.keys(stages).filter((k) => !STAGE_ORDER.includes(k) && k !== '_plan'),
  ];
  const isActive = run && ACTIVE.has(run.outcome);
  const needsApproval = run && run.outcome === 'awaiting_review' && !run.approvedAt;

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Listing Data Health', href: '/admin/reports/listing-data-health' },
          { name: 'Run' },
        ]} />
      </div>

      {error && <div className="text-sm text-red-700 my-4">{error}</div>}
      {!error && !run && <div className="text-sm text-gray-500 my-4">Loading…</div>}

      {run && (
        <div className="py-5 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl">
              {run.listing ? (
                <NavLink to={`/admin/listings/${run.listing.id}`} className="text-indigo-700 hover:underline">
                  {run.listing.name}
                </NavLink>
              ) : <span className="text-gray-400 italic">listing unknown</span>}
            </h1>
            <OutcomePill outcome={run.outcome} />
            {isActive && <span className="text-xs text-blue-600 animate-pulse">live · auto-refreshing</span>}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>kind <span className="font-mono">{run.kind}</span></span>
            <span>· trigger {run.triggeredBy}</span>
            <span>· {run.startedAt ? `started ${formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })}` : (run.createdAt ? `queued ${formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}` : '')}</span>
            {run.finishedAt && <span title={format(new Date(run.finishedAt), 'PPpp')}>· finished {formatDistanceToNow(new Date(run.finishedAt), { addSuffix: true })}</span>}
            <span>· id <code>{run.id.slice(0, 8)}</code></span>
          </div>

          {needsApproval && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between">
              <div className="text-sm text-amber-900">
                <strong>Proposal ready for review.</strong> Approving applies the listing-field autoPatch + writes wines/assets on the next supervisor tick.
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={reject} disabled={busy}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                  Reject
                </button>
                <button type="button" onClick={approve} disabled={busy}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50">
                  {busy ? 'Approving…' : 'Approve write'}
                </button>
              </div>
            </div>
          )}

          {run.error && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <CrawlFailureBadge error={run.error} showHint />
              <div className="mt-2 text-xs font-mono text-red-800 whitespace-pre-wrap break-all">
                {run.error}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Steps</h2>
            {orderedKeys.length === 0 ? (
              <div className="text-sm text-gray-500 italic">
                {isActive
                  ? 'Run is in progress — per-stage detail appears when it completes.'
                  : 'No stage detail recorded for this run.'}
              </div>
            ) : (
              <div className="space-y-2">
                {orderedKeys.map((k) => <StageStep key={k} stageKey={k} stage={stages[k]} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}
